import type { GeneratedSample } from './types';

/**
 * Synthesized NES-style snare drum.
 *
 * Recipe: white noise + a 180 Hz tonal layer ("body"), both shaped by a
 * fast decay envelope. The tonal layer decays faster than the noise so
 * you hear a sharp pitched attack followed by a noise tail — that's the
 * classic NES snare profile.
 */
export function generateSnare(sampleRate = 22_050): GeneratedSample {
  const durationSec = 0.12;
  const length = Math.floor(durationSec * sampleRate);
  const data = new Float32Array(length);

  const toneFreq = 180;
  const noiseDecay = 14;
  const toneDecay = 35;
  const noiseGain = 0.7;
  const toneGain = 0.5;

  // Cheap LCG so the snare sample is deterministic across rebuilds.
  let rng = 0x12345678;
  const nextNoise = () => {
    rng = (rng * 1664525 + 1013904223) >>> 0;
    return (rng / 0xffffffff) * 2 - 1;
  };

  let phase = 0;
  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    const noiseEnv = Math.exp(-noiseDecay * t);
    const toneEnv = Math.exp(-toneDecay * t);
    phase += (toneFreq * 2 * Math.PI) / sampleRate;
    const noise = nextNoise() * noiseGain * noiseEnv;
    const tone = Math.sin(phase) * toneGain * toneEnv;
    data[i] = Math.max(-1, Math.min(1, noise + tone));
  }

  return { data, sampleRate, name: 'snare' };
}
